import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { ChangeDetectionStrategy, Component, ElementRef, HostListener, Input, ViewChild } from '@angular/core';
import { BehaviorSubject, distinctUntilChanged, map, Observable, ReplaySubject, shareReplay, takeUntil } from 'rxjs';
import { calculateIfDivIsInView } from '../../util/helper';

export type Stat = {
  label: string;
  value: number;
};

export type Stats = {
  stat1: Stat;
  stat2: Stat;
  stat3: Stat;
};

@Component({
  selector: 'stats',
  templateUrl: './stats.component.html',
  styleUrls: ['./stats.component.scss', './stats.component.mobile.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class StatsComponent {

  @Input() stats: Stats;
  isMobile: boolean;

  statsVisible$ = new BehaviorSubject<boolean>(false);
  private readonly _statsVisible$: Observable<boolean> = this.statsVisible$.asObservable().pipe(distinctUntilChanged());
  private readonly _destroy$ = new ReplaySubject<void>(1);
  readonly stat1Number$: Observable<number>;
  readonly stat2Number$: Observable<number>;
  readonly stat3Number$: Observable<number>;

  @ViewChild('statsWrapper', { static: false }) private statsWrapperDivElement: ElementRef<HTMLDivElement>;

  @HostListener('window:scroll', ['$event'])
  isScrolledIntoView() {
    if (!this.statsWrapperDivElement) {
      return;
    }

    const rect = this.statsWrapperDivElement.nativeElement.getBoundingClientRect();
    const windowHeight = (window.innerHeight || document.documentElement.clientHeight);

    this.statsVisible$.next(calculateIfDivIsInView(rect, windowHeight, 10));
  }

  constructor(readonly breakpointObserver: BreakpointObserver) {
    this.stat1Number$ = this._statsVisible$.pipe(
      map(statsVisible => statsVisible ? this.stats.stat1.value : 0),
      distinctUntilChanged(),
      shareReplay(1)
    );

    this.stat2Number$ = this._statsVisible$.pipe(
      map(statsVisible => statsVisible ? this.stats.stat2.value : 0),
      distinctUntilChanged(),
      shareReplay(1)
    );

    this.stat3Number$ = this._statsVisible$.pipe(
      map(statsVisible => statsVisible ? this.stats.stat3.value : 0),
      distinctUntilChanged(),
      shareReplay(1)
    );
  }

  ngOnInit(): void {
    this.breakpointObserver
      .observe([Breakpoints.Small, Breakpoints.XSmall])
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (size) => {
          this.isMobile = size.matches;
        },
      });
  }

  getStatText(label: string): string {
    return this.isMobile ? label.replace(" ", "<br />") : label;
  }
}
